import { Type, X, Download, Lock, Calendar } from 'lucide-react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNotify } from 'ra-core';
import { useOfferCreativeDrawer } from './OfferCreativeDrawerContext';
import { downloadFile } from '../../../utils';
import {
  CTA_ASSET_NOTE,
  formatAssetName,
  isCtaAsset,
  sortCreativeTextAssets,
  stripHtml,
} from '../offerDrawer/creatives/helpers';
import CardField from '@/components/ui/CardField';
import ImageAssetItem from '@/components/ui/ImageAssetItem';
import TrackingLinks from '../offerDrawer/creatives/TrackingLinks';
import { DISPLAY_TIME_ZONE } from '../../../constants';

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: DISPLAY_TIME_ZONE,
  });

const RICH_TEXT_ASSETS = new Set([
  'offer_details',
  'steps_to_redeem',
  'terms_and_conditions',
]);

function OfferCreativeDrawer({
  isOfferAccepted,
  brandLogo,
  brandName = 'Brand',
}: {
  isOfferAccepted: boolean;
  brandLogo?: string;
  brandName?: string;
}) {
  const { isOpen, creative, closeDrawer } = useOfferCreativeDrawer();
  const notify = useNotify();

  const imageAssets =
    creative?.creative_assets.filter(
      a => a.asset_type === 'image' && a.image
    ) ?? [];
  const textAssets =
    creative?.creative_assets.filter(
      a => a.asset_type === 'text' && a.content
    ) ?? [];
  const titleContent = textAssets.find(
    a => a.name.toLowerCase() === 'title'
  )?.content;
  const visibleTextAssets = sortCreativeTextAssets(
    titleContent
      ? textAssets.filter(
          a =>
            !(
              a.name.toLowerCase() === 'condensed_title' &&
              a.content === titleContent
            )
        )
      : textAssets
  );

  const showBrandLogo = !!brandLogo;

  const handleDownloadAll = async () => {
    const assets =
      creative?.creative_assets.filter(
        a => a.asset_type === 'image' && a.image
      ) ?? [];
    const results = await Promise.all(
      assets.map(a =>
        downloadFile(a.image!.preview_url, a.image!.original_filename || a.name)
      )
    );
    if (results.some(ok => !ok)) {
      notify('Some images failed to download', { type: 'error' });
    }
  };

  return (
    <Drawer
      direction="right"
      open={isOpen}
      onOpenChange={open => !open && closeDrawer()}
      handleOnly
    >
      <DrawerContent>
        {/* Header — override default flex-col with flex-row */}
        <DrawerHeader className="shrink-0 bg-muted border-b border-border px-5 py-3.5 flex-row items-center justify-between h-[70px]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="size-8 bg-card border border-border rounded-md flex items-center justify-center shrink-0 text-muted-foreground">
              <Type className="size-4" />
            </div>
            <DrawerTitle className="text-base font-semibold truncate">
              {creative?.name}
            </DrawerTitle>
            {creative?.lang && (
              <Badge variant="primary" className="uppercase">
                {creative.lang}
              </Badge>
            )}
          </div>
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" strokeWidth={2.5} />
            </Button>
          </DrawerClose>
        </DrawerHeader>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-6">
          {(creative?.start_date || creative?.end_date) && (
            <div className="flex items-start gap-3 rounded-xl border border-amber/40 bg-amber/10 px-4 py-3">
              <Calendar className="size-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="flex flex-col gap-0.5 min-w-0">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                  Time-constrained creative
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                  {creative.start_date && creative.end_date
                    ? `Active from ${fmtDate(creative.start_date)} to ${fmtDate(creative.end_date)}.`
                    : creative.start_date
                      ? `Available from ${fmtDate(creative.start_date)}.`
                      : `Available until ${fmtDate(creative.end_date!)}.`}
                  {creative.creative_assets.some(
                    a => a.name.toLowerCase() === 'promo_code' && a.content
                  )
                    ? ' The promo code in this creative may not work outside this window — only publish within the specified dates.'
                    : ' Only publish this creative within the specified window.'}
                </p>
              </div>
            </div>
          )}

          {visibleTextAssets.length > 0 && (
            <div className="flex flex-col gap-3">
              {visibleTextAssets.map(asset => (
                <CardField
                  key={asset.id}
                  label={formatAssetName(asset.name)}
                  copyText={
                    RICH_TEXT_ASSETS.has(asset.name.toLowerCase())
                      ? stripHtml(asset.content)
                      : asset.content
                  }
                >
                  {RICH_TEXT_ASSETS.has(asset.name.toLowerCase()) ? (
                    <div
                      className="rich-text"
                      dangerouslySetInnerHTML={{ __html: asset.content }}
                    />
                  ) : (
                    <p className="text-sm text-foreground">{asset.content}</p>
                  )}
                  {asset.name.toLowerCase() === 'promo_code' && (
                    <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                      This promo code is essential to the campaign's success —
                      make sure it appears clearly in your content.
                    </p>
                  )}
                  {isCtaAsset(asset.name) && (
                    <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                      {CTA_ASSET_NOTE}
                    </p>
                  )}
                </CardField>
              ))}
            </div>
          )}

          {(imageAssets.length > 0 || showBrandLogo) && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Images
                </p>
                {imageAssets.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs gap-1.5 text-muted-foreground hover:text-foreground"
                    onClick={handleDownloadAll}
                  >
                    <Download className="size-3.5" />
                    Download all
                  </Button>
                )}
              </div>
              {imageAssets.map(
                asset =>
                  asset.image && (
                    <div
                      key={asset.id}
                      className="rounded-xl border border-border bg-secondary p-4"
                    >
                      <ImageAssetItem
                        url={asset.image.preview_url}
                        width={asset.image.width}
                        height={asset.image.height}
                        fileName={asset.name}
                      />
                    </div>
                  )
              )}
              {showBrandLogo && (
                <div className="rounded-xl border border-border bg-secondary p-4">
                  <ImageAssetItem url={brandLogo!} fileName="Brand logo" />
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col gap-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Tracking Links
            </p>
            {isOfferAccepted && creative?.trackinglinks ? (
              <TrackingLinks trackingLinks={creative.trackinglinks} />
            ) : (
              <div className="flex items-start gap-2.5 rounded-md border border-border bg-secondary px-3 py-3">
                <Lock className="size-3.5 text-muted-foreground shrink-0 mt-px" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Tracking links will be available once the offer is accepted.
                </p>
              </div>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export default OfferCreativeDrawer;
